import React, { useState, useEffect, useMemo, useRef } from 'react';
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { msalConfig, loginRequest, graphConfig } from "./config/authConfig";
import { translations } from "./translations/translations";
import SharePointService from './services/sharepoint.service';
import { BarChart3, FolderOpen, Users, Building2, FileText, Globe, LogOut, CheckCircle, XCircle, Save, Edit2, Trash2, X, Clock, Play, Square, Copy, Star, Plus, ChevronDown } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';  // ← NUR DAS! Kein "autoTable from"
import * as XLSX from 'xlsx';
import { travelBrainLogoBase64 } from './assets/travelbrainLogo';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const msalInstance = new PublicClientApplication(msalConfig);

// MSAL initialisieren und Redirect Promise verarbeiten
msalInstance.initialize().then(() => {
  console.log('MSAL initialized successfully');
  
  // JETZT Redirect Promise verarbeiten
  return msalInstance.handleRedirectPromise();
}).then((response) => {
  if (response !== null) {
    msalInstance.setActiveAccount(response.account);
    console.log('Redirect successful, account set:', response.account.username);
  } else {
    // Kein Redirect, check ob bereits eingeloggt
    const currentAccounts = msalInstance.getAllAccounts();
    if (currentAccounts.length > 0) {
      msalInstance.setActiveAccount(currentAccounts[0]);
      console.log('Existing account found:', currentAccounts[0].username);
    }
  }
}).catch((error) => {
  console.error('MSAL initialization or redirect error:', error);
});

// Global verfügbar machen für SharePoint Service
window.msalInstance = msalInstance;

function NavButton({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
      <Icon size={20} />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}

// ─── Sortier-Hilfsfunktion ────────────────────────────────────────────────────
function sortBy(arr, { key, dir }) {
  if (!key) return arr;
  return [...arr].sort((a, b) => {
    let av = a[key] ?? '', bv = b[key] ?? '';
    if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ─── Sortierbare Spalten-Header Komponente ────────────────────────────────────
function Th({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-4 py-3 text-left text-sm font-semibold cursor-pointer select-none hover:bg-gray-100 transition-colors group ${className}`}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className={`text-xs transition-colors ${active ? 'text-blue-600' : 'text-gray-300 group-hover:text-gray-400'}`}>
          {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}

function TimeTrackerApp() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [language, setLanguage] = useState('en');
  const [currentView, setCurrentView] = useState('dashboard');
  const [spService, setSpService] = useState(null);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [reportFilter, setReportFilter] = useState({
  customer: '',
  project: '',
  startDate: '',
  endDate: '',
  onlyBillable: false
});
  const [myReportFilter, setMyReportFilter] = useState({ project: '', startDate: '', endDate: '', onlyBillable: false });
  const [reportType, setReportType] = useState('custom'); // 'custom' | 'monthly' | 'customer' | 'employee' | 'budget'
  const [reportCustFilter, setReportCustFilter] = useState('');
  // Projektzuordnungen
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [showOnlyMyProjects, setShowOnlyMyProjects] = useState(false);
  const [managingEmployee, setManagingEmployee] = useState(null); // für Admin-Modal

  // ─── Sortier-States ───────────────────────────────────────────────────────
  const [teSort,   setTeSort]   = useState({ key: 'date', dir: 'desc' }); // Time Entries
  const [custSort, setCustSort] = useState({ key: 'name', dir: 'asc'  }); // Customers
  const [projSort, setProjSort] = useState({ key: 'name', dir: 'asc'  }); // Projects
  const [empSort,  setEmpSort]  = useState({ key: 'name', dir: 'asc'  }); // Employees

  const makeToggle = (setter) => (key) =>
    setter(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  // ─── Dashboard Chart-Daten (muss vor early returns stehen — Hook-Regel) ──
  const dashboardData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const CHART_COLORS_INNER = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];

    const isAdmin = currentEmployee?.role === 'Admin';

    // Für Nicht-Admins: nur Einträge der zugewiesenen Projekte anzeigen
    const assignedProjectIds = projectAssignments
      .filter(a => Number(a.employeeId) === Number(currentEmployee?.id))
      .map(a => Number(a.projectId));
    const assignedProjectNames = projects
      .filter(p => isAdmin || assignedProjectIds.length === 0 || assignedProjectIds.includes(Number(p.id)))
      .map(p => p.name);
    const filteredEntries = isAdmin
      ? timeEntries
      : timeEntries.filter(e => assignedProjectNames.includes(e.project));

    const billableH = filteredEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
    const nonBillableH = filteredEntries.filter(e => !e.billable).reduce((sum, e) => sum + e.hours, 0);

    // 1) Stunden pro Projekt
    const projectMap = {};
    filteredEntries.forEach(e => { if (e.project) projectMap[e.project] = (projectMap[e.project] || 0) + e.hours; });
    const hoursPerProject = Object.entries(projectMap)
      .map(([name, hours]) => ({ name: name.length > 16 ? name.slice(0, 14) + '…' : name, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours);

    // 2) Stunden pro Woche (letzte 12 Wochen)
    const getWeekLabel = (dateStr) => {
      const d = new Date(dateStr);
      const startOfYear = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
      return `KW${week}`;
    };
    const weekMap = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i * 7);
      weekMap[getWeekLabel(d.toISOString().split('T')[0])] = 0;
    }
    filteredEntries.forEach(e => { if (e.date) { const l = getWeekLabel(e.date); if (l in weekMap) weekMap[l] += e.hours; } });
    const hoursPerWeek = Object.entries(weekMap).map(([week, hours]) => ({ week, hours: Math.round(hours * 100) / 100 }));

    // 3) Billable vs Non-Billable
    const billableVsNon = [
      { name: 'Billable', value: Math.round(billableH * 100) / 100, color: '#10b981' },
      { name: 'Non-Billable', value: Math.round(nonBillableH * 100) / 100, color: '#6b7280' }
    ].filter(d => d.value > 0);

    // 4) Top 5 Projekte diesen Monat
    const monthMap = {};
    filteredEntries.forEach(e => {
      if (!e.date || !e.project) return;
      const d = new Date(e.date);
      if (d.getMonth() === currentMonth && d.getFullYear() === currentYear)
        monthMap[e.project] = (monthMap[e.project] || 0) + e.hours;
    });
    const top5ThisMonth = Object.entries(monthMap)
      .map(([name, hours]) => ({ name, hours: Math.round(hours * 100) / 100 }))
      .sort((a, b) => b.hours - a.hours).slice(0, 5);

    // 5) Heatmap: letzte 91 Tage
    const heatmapMap = {};
    filteredEntries.forEach(e => { if (e.date) heatmapMap[e.date] = (heatmapMap[e.date] || 0) + e.hours; });
    const heatmapDays = [];
    for (let i = 90; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      heatmapDays.push({ date: dateStr, hours: heatmapMap[dateStr] || 0 });
    }

    return { hoursPerProject, hoursPerWeek, billableVsNon, top5ThisMonth, heatmapDays, CHART_COLORS_INNER, isAdmin };
  }, [timeEntries, currentEmployee, projectAssignments, projects]);



// Gefilterte Einträge für Reports
function getFilteredReportEntries() {
  console.log('=== FILTER DEBUG ===');
  console.log('Current Filter:', reportFilter);
  console.log('Available Projects:', projects);
  console.log('Available TimeEntries:', timeEntries);
  
  return timeEntries.filter(entry => {
    const entryDate = new Date(entry.date);
    const startDate = reportFilter.startDate ? new Date(reportFilter.startDate) : null;
    const endDate = reportFilter.endDate ? new Date(reportFilter.endDate) : null;
    
    // Datum-Filter
    if (startDate && entryDate < startDate) {
      console.log(`Entry ${entry.id} filtered out: date before start`);
      return false;
    }
    if (endDate && entryDate > endDate) {
      console.log(`Entry ${entry.id} filtered out: date after end`);
      return false;
    }
    
    // Projekt-Filter
    if (reportFilter.project && entry.project !== reportFilter.project) {
      console.log(`Entry ${entry.id} filtered out: project mismatch (${entry.project} !== ${reportFilter.project})`);
      return false;
    }
    
    // Kunden-Filter
    if (reportFilter.customer) {
      const project = projects.find(p => p.name === entry.project);
      console.log(`Entry ${entry.id}: Looking for project "${entry.project}"`, project);
      
      if (!project) {
        console.log(`Entry ${entry.id} filtered out: project not found`);
        return false;
      }
      
      if (project.customer !== reportFilter.customer) {
        console.log(`Entry ${entry.id} filtered out: customer mismatch (${project.customer} !== ${reportFilter.customer})`);
        return false;
      }
      
      console.log(`Entry ${entry.id} PASSED customer filter`);
    }
    
    // Billable-Filter
    if (reportFilter.onlyBillable && !entry.billable) {
      console.log(`Entry ${entry.id} filtered out: not billable`);
      return false;
    }
    
    console.log(`Entry ${entry.id} PASSED all filters`);
    return true;
  });
}

  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    project: '',
    activity: '',
    hours: '',
    billable: true,
    description: ''
  });

  const t = translations[language];



const handleLogin = async () => {
  try {
    // Prüfen ob wir in einem iFrame sind (SharePoint)
    const isInIframe = window.self !== window.top;
    
    if (isInIframe) {
      // SharePoint: Öffne Login in neuem Fenster
      console.log('iFrame detected, opening login in new window');
      const loginUrl = window.location.origin;
      const loginWindow = window.open(
        loginUrl, 
        'TimeTracker Login', 
        'width=800,height=600,menubar=no,toolbar=no,location=no'
      );
      
      if (!loginWindow) {
        alert('Popup wurde blockiert! Bitte erlauben Sie Popups für diese Seite.');
        return;
      }
      
      // Listener für Message vom Popup
      window.addEventListener('message', (event) => {
        // Sicherheit: Nur Messages von der eigenen App-Domain akzeptieren
        if (event.origin === window.location.origin) {
          if (event.data.type === 'LOGIN_SUCCESS') {
            console.log('Login successful message received from popup');
            // Popup schließen
            if (loginWindow && !loginWindow.closed) {
              loginWindow.close();
            }
            // SharePoint-Seite neu laden
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        }
      }, { once: true });
      
      // Fallback: Popup manuell überwachen
      const checkWindow = setInterval(() => {
        if (loginWindow.closed) {
          clearInterval(checkWindow);
          console.log('Login window closed manually');
          // Nach 1 Sekunde neu laden
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }, 1000);
      
    } else {
      // Normaler Zugriff (Vercel): Redirect
      console.log('Direct access, using loginRedirect');
      await instance.loginRedirect(loginRequest);
    }
  } catch (error) {
    console.error("Login error:", error);
    alert('Login fehlgeschlagen: ' + error.message);
  }
};

const handleLogout = () => { 
  instance.logoutRedirect(); 
};

  useEffect(() => {
    
       
    if (accounts.length > 0) {
      console.log("Account detected:", accounts[0].username);
      const request = { ...loginRequest, account: accounts[0], forceRefresh: false };
      instance.acquireTokenSilent(request).then((response) => {
        console.log("Token acquired");
        const service = new SharePointService(response.accessToken, graphConfig.siteId);
        setSpService(service);
        service.getCurrentEmployee(accounts[0].username).then(emp => {
          console.log("Employee found:", emp);
          setCurrentEmployee(emp);
          loadAllData(service, emp);
        }).catch(err => {
          console.error("Error getting employee:", err);
          alert("Could not find employee record.");
        });
      }).catch((error) => {
        console.error("Silent token acquisition failed:", error);
        if (error.errorCode === "consent_required" || error.errorCode === "interaction_required" || error.errorCode === "login_required") {
          instance.acquireTokenPopup(request).then((response) => {
            const service = new SharePointService(response.accessToken, graphConfig.siteId);
            setSpService(service);
          }).catch(e => console.error("Popup failed:", e));
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance]);

  const loadAllData = async (service = spService, employee = currentEmployee) => {
    if (!service || !employee) return;
    setLoading(true);
    try {
      console.log("Loading data...");
      const [act, cust, emp, proj, time, assignments] = await Promise.all([
        service.getActivities(language),
        service.getCustomers(),
        service.getEmployees(),
        service.getProjects(),
        service.getTimeEntries(employee.role === 'Employee' ? employee.id : null),
        service.getProjectAssignments()
      ]);
      console.log("Data loaded:", { activities: act.length, customers: cust.length, projects: proj.length, timeEntries: time.length, assignments: assignments.length });
      setActivities(act);
      setCustomers(cust);
      setEmployees(emp);
      setProjects(proj);
      setTimeEntries(time);
      setProjectAssignments(assignments);
      // Employees sehen standardmässig nur ihre eigenen Projekte
      if (employee.role === 'Employee') setShowOnlyMyProjects(true);
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Error loading data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Projektzuordnung ────────────────────────────────────────────────────

  // Projekte die der aktuelle Employee zugeteilt bekommt
  // WICHTIG: IDs als Number vergleichen (SP gibt Strings zurück, localStorage speichert Numbers)
  const myAssignedProjectIds = projectAssignments
    .filter(a => Number(a.employeeId) === Number(currentEmployee?.id))
    .map(a => Number(a.projectId));

  // Sichtbare Projekte: gefiltert nach Rolle / Toggle
  const visibleProjects = projects.filter(p => {
    if (!showOnlyMyProjects || currentEmployee?.role === 'Admin') return true;
    // Wenn keine Zuordnungen vorhanden → alle zeigen (Fallback damit kein leerer Bildschirm)
    if (myAssignedProjectIds.length === 0) return true;
    return myAssignedProjectIds.includes(Number(p.id));
  });

  // ─── "Meine Reports" (Mitarbeitende) ─────────────────────────────────────
  // Strikt: nur tatsächlich zugewiesene Projekte (kein "alle"-Fallback wie beim Dashboard)
  const myReportProjects = projects.filter(p => myAssignedProjectIds.includes(Number(p.id)));

  const getMyReportEntries = () => {
    const allowed = myReportFilter.project
      ? myReportProjects.filter(p => p.name === myReportFilter.project)
      : myReportProjects;
    const names = allowed.map(p => p.name);
    const norm = (s) => (s || '').toLowerCase().trim();
    const myEmail = norm(currentEmployee?.email);
    const myName = norm(currentEmployee?.name);
    // Nur eigene Stunden: per E-Mail zuordnen, sonst über den Namen
    const isMine = (e) => (myEmail && norm(e.employeeEmail) ? norm(e.employeeEmail) === myEmail : !!myName && norm(e.employee) === myName);
    return timeEntries.filter(e => {
      if (!names.includes(e.project)) return false;
      if (!isMine(e)) return false;
      if (myReportFilter.startDate && e.date < myReportFilter.startDate) return false;
      if (myReportFilter.endDate && e.date > myReportFilter.endDate) return false;
      if (myReportFilter.onlyBillable && !e.billable) return false;
      return true;
    }).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  };

  const setMyReportPeriod = (period) => {
    const now = new Date();
    const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    let start, end;
    if (period === 'thisMonth') { start = new Date(now.getFullYear(), now.getMonth(), 1); end = new Date(now.getFullYear(), now.getMonth() + 1, 0); }
    else if (period === 'lastMonth') { start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); }
    else if (period === 'thisYear') { start = new Date(now.getFullYear(), 0, 1); end = new Date(now.getFullYear(), 11, 31); }
    else { setMyReportFilter(f => ({ ...f, startDate: '', endDate: '' })); return; }
    setMyReportFilter(f => ({ ...f, startDate: iso(start), endDate: iso(end) }));
  };

  const handleMyReportPDF = () => {
    const proj = myReportProjects.find(p => p.name === myReportFilter.project);
    return generatePdfReport(getMyReportEntries(), myReportFilter, proj?.customer || '');
  };

  // Admin: Projektzuordnung für einen Employee umschalten
  const handleToggleAssignment = async (employee, project) => {
    const existing = projectAssignments.find(
      a => Number(a.employeeId) === Number(employee.id) && Number(a.projectId) === Number(project.id)
    );
    if (existing) {
      await spService.removeEmployeeFromProject(existing.id);
      setProjectAssignments(prev => prev.filter(a => a.id !== existing.id));
    } else {
      const newAssignment = await spService.assignEmployeeToProject(
        Number(employee.id), Number(project.id)
      );
      setProjectAssignments(prev => [...prev, newAssignment]);
    }
  };

  const handleSaveEntry = async (e) => {
  e.preventDefault();

  if (!newEntry.project || !newEntry.activity || !newEntry.hours) {
    alert('Please fill all required fields');
    return;
  }
  
  setLoading(true);
  
  try {
    const projectObj = projects.find(p => p.name === newEntry.project);
    
    // Bereite den Entry vor
    const entryToSave = {
      date: newEntry.date,
      project: newEntry.project,
      projectId: projectObj?.id,
      activity: newEntry.activity,
      hours: parseFloat(newEntry.hours),
      description: newEntry.description,
      billable: newEntry.billable,
      employee: currentEmployee?.name,
      employeeEmail: currentEmployee?.email
    };
    
    // Speichern über SharePointService
    await SharePointService.saveTimeEntry(entryToSave);
    
    alert('Time entry saved!');
    
    // Formular zurücksetzen
    setNewEntry({ 
      date: new Date().toISOString().split('T')[0], 
      project: '', 
      activity: '', 
      hours: '', 
      description: '', 
      billable: true 
    });
    
    // Daten neu laden
    await loadAllData();
    
  } catch (error) {
    console.error('Error saving entry:', error);
    alert('Error occurred!');
  } finally {
    setLoading(false);
  }
};

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      date: entry.date,
      project: entry.project,
      activity: entry.activity,
      hours: entry.hours.toString(),
      billable: entry.billable,
      description: entry.description
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.activity || !newEntry.hours) {
      alert('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await spService.updateTimeEntry(editingEntry.id, newEntry);
      alert(t.entryUpdated);
      setNewEntry({ date: new Date().toISOString().split('T')[0], project: '', activity: '', hours: '', billable: true, description: '' });
      setEditingEntry(null);
      await loadAllData();
    } catch (error) {
      console.error('Error updating entry:', error);
      alert(t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm(t.confirmDelete)) return;
    setLoading(true);
    try {
      await spService.deleteTimeEntry(entryId);
      alert(t.entryDeleted);
      await loadAllData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert(t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setNewEntry({ date: new Date().toISOString().split('T')[0], project: '', activity: '', hours: '', billable: true, description: '' });
  };

  // ─── Bulk-Operations ──────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (id) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleSelectAll = (entries) => {
    if (selectedIds.size === entries.length && entries.length > 0)
      setSelectedIds(new Set());
    else
      setSelectedIds(new Set(entries.map(e => e.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} Einträge wirklich löschen?`)) return;
    setLoading(true);
    try {
      await Promise.all([...selectedIds].map(id => spService.deleteTimeEntry(id)));
      setSelectedIds(new Set());
      await loadAllData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert(t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSetBillable = async (billable) => {
    if (selectedIds.size === 0) return;
    setLoading(true);
    try {
      const entriesToUpdate = timeEntries.filter(e => selectedIds.has(e.id));
      await Promise.all(entriesToUpdate.map(e => spService.updateTimeEntry(e.id, { ...e, billable })));
      setSelectedIds(new Set());
      await loadAllData();
    } catch (error) {
      console.error('Bulk billable error:', error);
      alert(t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };


  // ─── Quick-Add / Timer / Templates ───────────────────────────────────────

  // Templates: gespeichert in localStorage
  const [templates, setTemplates] = useState(() =>
    JSON.parse(localStorage.getItem('quickTemplates') || '[]')
  );
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false); // eslint-disable-line no-unused-vars
  const quickAddRef = useRef(null);
  const templatesRef = useRef(null);

  // Schliessen bei Klick außerhalb
  useEffect(() => {
    const handler = (e) => {
      if (quickAddRef.current && !quickAddRef.current.contains(e.target)) setShowQuickAdd(false);
      if (templatesRef.current && !templatesRef.current.contains(e.target)) setShowTemplates(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Timer
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStart, setTimerStart] = useState(null); // eslint-disable-line no-unused-vars
  const [timerElapsed, setTimerElapsed] = useState(0); // Sekunden
  const [timerProject, setTimerProject] = useState('');
  const [timerActivity, setTimerActivity] = useState('');
  const timerIntervalRef = useRef(null);

  const formatTimer = (secs) => {
    const h = Math.floor(secs / 3600).toString().padStart(2, '0');
    const m = Math.floor((secs % 3600) / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const handleStartTimer = () => {
    if (!timerProject || !timerActivity) {
      alert('Bitte zuerst Projekt und Aktivität wählen.');
      return;
    }
    setTimerStart(new Date());
    setTimerElapsed(0);
    setTimerRunning(true);
    timerIntervalRef.current = setInterval(() => setTimerElapsed(s => s + 1), 1000);
  };

  const handleStopTimer = async () => {
    clearInterval(timerIntervalRef.current);
    setTimerRunning(false);
    const hours = Math.round((timerElapsed / 3600) * 4) / 4; // runden auf 0.25
    if (hours < 0.25) { setTimerElapsed(0); return; }
    const entry = {
      date: new Date().toISOString().split('T')[0],
      project: timerProject,
      activity: timerActivity,
      hours,
      billable: true,
      description: 'Via Timer',
      employee: currentEmployee?.name,
      employeeEmail: currentEmployee?.email
    };
    setLoading(true);
    try {
      await SharePointService.saveTimeEntry(entry);
      await loadAllData();
      setTimerElapsed(0);
      alert(`✅ ${hours}h gespeichert!`);
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Letzten Eintrag duplizieren
  const handleDuplicate = async (entry) => {
    const dupe = {
      ...entry,
      date: new Date().toISOString().split('T')[0],
      employee: currentEmployee?.name,
      employeeEmail: currentEmployee?.email
    };
    setLoading(true);
    try {
      await SharePointService.saveTimeEntry(dupe);
      await loadAllData();
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Template speichern aus aktuellem Formular
  const handleSaveTemplate = () => {
    if (!newEntry.project || !newEntry.activity || !newEntry.hours) {
      alert('Formular zuerst ausfüllen.');
      return;
    }
    const name = window.prompt('Template-Name:', `${newEntry.project} – ${newEntry.activity}`);
    if (!name) return;
    const tmpl = {
      id: Date.now(),
      name,
      project: newEntry.project,
      activity: newEntry.activity,
      hours: newEntry.hours,
      billable: newEntry.billable,
      description: newEntry.description
    };
    const updated = [...templates, tmpl];
    setTemplates(updated);
    localStorage.setItem('quickTemplates', JSON.stringify(updated));
  };

  const handleDeleteTemplate = (id) => {
    const updated = templates.filter(t => t.id !== id);
    setTemplates(updated);
    localStorage.setItem('quickTemplates', JSON.stringify(updated));
  };

  // Template anwenden → Formular befüllen + zu Time Tracking wechseln
  const handleApplyTemplate = (tmpl) => {
    setNewEntry({
      date: new Date().toISOString().split('T')[0],
      project: tmpl.project,
      activity: tmpl.activity,
      hours: tmpl.hours,
      billable: tmpl.billable,
      description: tmpl.description || ''
    });
    setCurrentView('timeTracking');
    setShowTemplates(false);
    setShowQuickAdd(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Quick-Entry: Template direkt speichern ohne Formular
  const handleQuickSave = async (tmpl) => {
    const entry = {
      date: new Date().toISOString().split('T')[0],
      project: tmpl.project,
      activity: tmpl.activity,
      hours: parseFloat(tmpl.hours),
      billable: tmpl.billable,
      description: tmpl.description || '',
      employee: currentEmployee?.name,
      employeeEmail: currentEmployee?.email
    };
    setLoading(true);
    try {
      await SharePointService.saveTimeEntry(entry);
      await loadAllData();
      setShowQuickAdd(false);
      alert(`✅ "${tmpl.name}" gespeichert!`);
    } catch (err) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

const handlePDFExport = () => generatePdfReport(getFilteredReportEntries(), reportFilter, reportFilter.customer);

// Gemeinsame PDF-Erzeugung für Admin-Report und "Meine Reports"
const generatePdfReport = async (filteredEntries, reportFilter, customerNameOverride) => {
  if (filteredEntries.length === 0) {
    alert('No entries to export! Please adjust filters.');
    return;
  }

  setLoading(true);
  
  try {
    // Berechne Summen
    const totalHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);
    const billableHours = filteredEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
    const nonBillableHours = totalHours - billableHours;
    
    // Customer Name basierend auf Sprache
    const customerLabel = language === 'de' ? 'Kunde' : language === 'fr' ? 'Client' : 'Customer';
    const allClientsLabel = language === 'de' ? 'Alle Kunden' : language === 'fr' ? 'Tous les clients' : 'All Clients';
    const customerName = customerNameOverride || allClientsLabel;
    
    // PDF erstellen
const doc = new jsPDF();

// TravelBrain Logo oben rechts
doc.setFontSize(9);
doc.setTextColor(120, 120, 120);
doc.text('powered by', 155, 14);
// Logo: Originalformat 1108x476px → skaliert auf ca. 40x17mm
doc.addImage(travelBrainLogoBase64, 'PNG', 148, 16, 50, 21);

// Zurück zu schwarz und normal
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

// Report Titel links
doc.setFontSize(20);
doc.setFont(undefined, 'bold');
doc.text('TimeTracker Pro', 14, 20);

doc.setFont(undefined, 'normal');
doc.setFontSize(14);
doc.text('Time Tracking Report', 14, 27);

doc.setFontSize(10);
doc.setTextColor(100, 100, 100);
doc.text(`Generated: ${new Date().toLocaleDateString('de-DE')}`, 14, 33);

doc.setTextColor(0, 0, 0);

let yPos = 42;
    
    // Customer Info Box
    doc.setFillColor(239, 246, 255); // bg-blue-50
    doc.setDrawColor(59, 130, 246); // border-blue-500
    doc.setLineWidth(0.5);
    doc.rect(14, yPos, 182, 20, 'FD');
    
    doc.setFontSize(12);
    doc.setTextColor(30, 64, 175); // text-blue-800
    doc.text(customerLabel + ':', 18, yPos + 8);
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 58, 138); // text-blue-900
    doc.text(customerName, 18, yPos + 16);
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    yPos += 30;
    
    // Filter Info
    if (reportFilter.project || reportFilter.startDate || reportFilter.endDate) {
      doc.setFontSize(12);
      doc.text('Filter Criteria:', 14, yPos);
      yPos += 7;
      
      doc.setFontSize(10);
      if (reportFilter.project) {
        doc.text(`Project: ${reportFilter.project}`, 20, yPos);
        yPos += 6;
      }
      if (reportFilter.startDate) {
        doc.text(`From: ${reportFilter.startDate}`, 20, yPos);
        yPos += 6;
      }
      if (reportFilter.endDate) {
        doc.text(`To: ${reportFilter.endDate}`, 20, yPos);
        yPos += 6;
      }
      if (reportFilter.onlyBillable) {
        doc.text('Only Billable Hours', 20, yPos);
        yPos += 6;
      }
      yPos += 5;
    }
    
    // Summary Table
    doc.setFontSize(12);
    doc.text('Summary', 14, yPos);
    yPos += 7;
    
    doc.autoTable({
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Total Entries', filteredEntries.length.toString()],
        ['Total Hours', `${totalHours.toFixed(2)}h`],
        ['Billable Hours', `${billableHours.toFixed(2)}h`],
        ['Non-Billable Hours', `${nonBillableHours.toFixed(2)}h`]
      ],
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235] }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
    
    // Time Entries Table
    doc.setFontSize(12);
    doc.text('Time Entries', 14, yPos);
    yPos += 7;
    
    const tableData = filteredEntries.map(e => [
      e.date,
      e.project,
      e.activity,
      e.employee || '-',
      `${e.hours}h`,
      e.billable ? 'Yes' : 'No',
      e.description || '-'
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Date', 'Project', 'Activity', 'Employee', 'Hours', 'Billable', 'Description']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [31, 41, 55] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 35 },
        2: { cellWidth: 28 },
        3: { cellWidth: 30 },
        4: { cellWidth: 15 },
        5: { cellWidth: 18 },
        6: { cellWidth: 'auto' }
      }
    });
    
    // Download
    const fileProject = reportFilter.project ? '_' + reportFilter.project.replace(/[^\w-]+/g, '_') : '';
    doc.save(`TimeTracker_Report${fileProject}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    alert('PDF Report successfully created!');
    
  } catch (error) {
    console.error('PDF Export error:', error);
    alert('Error creating PDF: ' + error.message);
  } finally {
    setLoading(false);
  }
};

const handleExcelExport = async () => {
  const filteredEntries = getFilteredReportEntries();
  
  if (filteredEntries.length === 0) {
    alert('No entries to export! Please adjust filters.');
    return;
  }

  setLoading(true);
  
  try {
    // Berechne Summen
    const totalHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);
    const billableHours = filteredEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
    const nonBillableHours = totalHours - billableHours;
    
    // Workbook erstellen
    const wb = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ['TimeTracker Pro Report'],
      [],
      ['Generated:', new Date().toLocaleDateString('de-DE')],
      [],
      ['Summary'],
      ['Metric', 'Value'],
      ['Total Entries', filteredEntries.length],
      ['Total Hours', totalHours.toFixed(2)],
      ['Billable Hours', billableHours.toFixed(2)],
      ['Non-Billable Hours', nonBillableHours.toFixed(2)]
    ];
    
    if (reportFilter.customer || reportFilter.project || reportFilter.startDate || reportFilter.endDate) {
      summaryData.push([]);
      summaryData.push(['Filters Applied']);
      if (reportFilter.customer) summaryData.push(['Customer:', reportFilter.customer]);
      if (reportFilter.project) summaryData.push(['Project:', reportFilter.project]);
      if (reportFilter.startDate) summaryData.push(['From:', reportFilter.startDate]);
      if (reportFilter.endDate) summaryData.push(['To:', reportFilter.endDate]);
      if (reportFilter.onlyBillable) summaryData.push(['Only Billable Hours']);
    }
    
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    
    // Time Entries Sheet
    const entriesData = [
      ['Date', 'Project', 'Activity', 'Hours', 'Billable', 'Employee', 'Description']
    ];
    
    filteredEntries.forEach(e => {
      entriesData.push([
        e.date,
        e.project,
        e.activity,
        e.hours,
        e.billable ? 'Yes' : 'No',
        e.employee || '-',
        e.description || '-'
      ]);
    });
    
    const wsEntries = XLSX.utils.aoa_to_sheet(entriesData);
    XLSX.utils.book_append_sheet(wb, wsEntries, 'Time Entries');
    
    // Download
    XLSX.writeFile(wb, `TimeTracker_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    alert('Excel Report successfully created!');
    
  } catch (error) {
    console.error('Excel Export error:', error);
    alert('Error creating Excel: ' + error.message);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (isAuthenticated && window.opener && !window.opener.closed) {
    console.log('Authenticated in popup, sending message to parent');
    window.opener.postMessage({ type: 'LOGIN_SUCCESS' }, 'https://travelbrain.sharepoint.com');
  }
}, [isAuthenticated]);

  if (!isAuthenticated) {
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center bg-white p-12 rounded-2xl shadow-xl max-w-md">
          <img src="https://travelbrain.chat.byteai.at/images/69055085570257c44872589e/ce1fa7a0-d2a7-4727-818e-9e0fa233a979__logo_1108x476px_RGB.png" alt="TravelBrain" className="h-20 mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-2 text-gray-900">{t.appTitle}</h1>
          <p className="text-gray-600 mb-8">{t.appSubtitle}</p>
          <button onClick={handleLogin} className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center gap-3">
            <svg className="w-6 h-6" viewBox="0 0 23 23" fill="currentColor"><path d="M11.03 0v11.03H0V0h11.03zm11.94 0v11.03H11.94V0h11.03zM11.03 11.94V23H0V11.94h11.03zm11.94 0V23H11.94V11.94h11.03z"/></svg>
            {t.signIn}
          </button>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Globe size={18} className="text-gray-400" />
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="text-sm border rounded px-2 py-1">
              <option value="en">🇬🇧 English</option>
              <option value="de">🇩🇪 Deutsch</option>
              <option value="fr">🇫🇷 Français</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !timeEntries.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Dashboard-KPIs: Admins sehen alle Daten, Employees nur ihre zugewiesenen Projekte
  const dashboardEntries = (() => {
    if (currentEmployee?.role === 'Admin') return timeEntries;
    const myProjIds = projectAssignments
      .filter(a => Number(a.employeeId) === Number(currentEmployee?.id))
      .map(a => Number(a.projectId));
    const myProjNames = projects
      .filter(p => myProjIds.length === 0 || myProjIds.includes(Number(p.id)))
      .map(p => p.name);
    return timeEntries.filter(e => myProjNames.includes(e.project));
  })();

  const today = new Date().toISOString().split('T')[0];
  const totalHoursToday = dashboardEntries.filter(e => e.date === today).reduce((sum, e) => sum + e.hours, 0);
  const totalHours = dashboardEntries.reduce((sum, e) => sum + e.hours, 0);
  const billableHours = dashboardEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
  const nonBillableHours = dashboardEntries.filter(e => !e.billable).reduce((sum, e) => sum + e.hours, 0);

  const CHART_COLORS = dashboardData.CHART_COLORS_INNER;
  const heatmapColor = (hours) => {
    if (hours === 0) return '#f3f4f6';
    if (hours < 2) return '#bfdbfe';
    if (hours < 4) return '#60a5fa';
    if (hours < 7) return '#2563eb';
    return '#1e3a8a';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="flex items-center gap-3">
                <img src="https://travelbrain.chat.byteai.at/images/69055085570257c44872589e/ce1fa7a0-d2a7-4727-818e-9e0fa233a979__logo_1108x476px_RGB.png" alt="TravelBrain" className="h-10" />
                <div className="border-l border-gray-300 pl-3">
                  <span className="text-2xl font-bold text-gray-900">{t.appTitle}</span>
                </div>
              </h1>
              <p className="text-sm text-gray-600 ml-16">{t.appSubtitle}</p>
            </div>
            <div className="flex items-center gap-3">

              {/* ── Timer-Anzeige (wenn läuft) ── */}
              {timerRunning && (
                <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="font-mono text-sm font-bold text-red-700">{formatTimer(timerElapsed)}</span>
                  <button onClick={handleStopTimer} className="p-1 text-red-600 hover:bg-red-100 rounded" title="Timer stoppen">
                    <Square size={14} />
                  </button>
                </div>
              )}

              {/* ── Quick-Add Dropdown ── */}
              <div className="relative" ref={quickAddRef}>
                <button
                  onClick={() => { setShowQuickAdd(v => !v); setShowTemplates(false); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  <Plus size={18} />
                  <span className="hidden md:inline">Quick-Add</span>
                  <ChevronDown size={14} />
                </button>

                {showQuickAdd && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b bg-gray-50">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Quick-Add</p>
                    </div>

                    {/* Letzten Eintrag duplizieren */}
                    {timeEntries.length > 0 && (
                      <div className="p-3 border-b">
                        <p className="text-xs text-gray-400 mb-2">Letzten Eintrag duplizieren</p>
                        <button
                          onClick={() => handleDuplicate(timeEntries[0])}
                          disabled={loading}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50 border border-dashed border-blue-200 text-left transition-colors"
                        >
                          <Copy size={16} className="text-blue-500 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 truncate">{timeEntries[0].project}</p>
                            <p className="text-xs text-gray-500">{timeEntries[0].activity} · {timeEntries[0].hours}h · {timeEntries[0].date}</p>
                          </div>
                        </button>
                      </div>
                    )}

                    {/* Timer */}
                    <div className="p-3 border-b">
                      <p className="text-xs text-gray-400 mb-2">Timer starten</p>
                      <div className="space-y-2">
                        <select value={timerProject} onChange={e => setTimerProject(e.target.value)} className="w-full px-3 py-1.5 text-sm border rounded-lg">
                          <option value="">Projekt wählen…</option>
                          {visibleProjects.filter(p => p.status === 'Active' || p.status === 'Aktiv').map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                        <select value={timerActivity} onChange={e => setTimerActivity(e.target.value)} className="w-full px-3 py-1.5 text-sm border rounded-lg">
                          <option value="">Aktivität wählen…</option>
                          {activities.map(a => <option key={a.id} value={a.title}>{a.title}</option>)}
                        </select>
                        {timerRunning ? (
                          <button onClick={handleStopTimer} className="w-full flex items-center justify-center gap-2 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm">
                            <Square size={14} /> Stop & Speichern ({formatTimer(timerElapsed)})
                          </button>
                        ) : (
                          <button onClick={handleStartTimer} className="w-full flex items-center justify-center gap-2 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm">
                            <Play size={14} /> Timer starten
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Templates */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-400">Favoriten / Templates</p>
                        <button
                          onClick={() => { handleApplyTemplate({ project: '', activity: '', hours: '', billable: true, description: '' }); setCurrentView('timeTracking'); setShowQuickAdd(false); }}
                          className="text-xs text-blue-600 hover:underline"
                        >+ Neu</button>
                      </div>
                      {templates.length === 0 ? (
                        <p className="text-xs text-gray-400 italic text-center py-2">
                          Noch keine Templates. Formular ausfüllen → ⭐ speichern.
                        </p>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {templates.map(tmpl => (
                            <div key={tmpl.id} className="flex items-center gap-2 group">
                              <button
                                onClick={() => handleQuickSave(tmpl)}
                                disabled={loading}
                                className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-emerald-50 text-left transition-colors min-w-0"
                              >
                                <Star size={14} className="text-amber-400 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{tmpl.name}</p>
                                  <p className="text-xs text-gray-500">{tmpl.project} · {tmpl.hours}h</p>
                                </div>
                              </button>
                              <button
                                onClick={() => handleApplyTemplate(tmpl)}
                                className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" title="Im Formular öffnen"
                              ><Edit2 size={12} /></button>
                              <button
                                onClick={() => handleDeleteTemplate(tmpl.id)}
                                className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" title="Template löschen"
                              ><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Sprache ── */}
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-gray-50">
                <Globe size={18} className="text-gray-600" />
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer">
                  <option value="en">🇬🇧 EN</option>
                  <option value="de">🇩🇪 DE</option>
                  <option value="fr">🇫🇷 FR</option>
                </select>
              </div>
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900">{currentEmployee?.name || accounts[0]?.name}</p>
                <p className="text-xs text-gray-500">{currentEmployee?.role || 'Employee'}</p>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <LogOut size={20} />
                <span className="hidden md:inline">{t.signOut}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto">
            <NavButton icon={BarChart3} label={t.dashboard} active={currentView === 'dashboard'} onClick={() => setCurrentView('dashboard')} />
            <NavButton icon={Clock} label={t.timeTracking} active={currentView === 'timeTracking'} onClick={() => setCurrentView('timeTracking')} />
            {currentEmployee?.role === 'Admin' && <NavButton icon={FolderOpen} label={t.projects} active={currentView === 'projects'} onClick={() => setCurrentView('projects')} />}
            {currentEmployee?.role === 'Admin' && <NavButton icon={Building2} label={t.customers} active={currentView === 'customers'} onClick={() => setCurrentView('customers')} />}
            {currentEmployee?.role === 'Admin' && <NavButton icon={Users} label={t.employees} active={currentView === 'employees'} onClick={() => setCurrentView('employees')} />}
            {currentEmployee?.role === 'Admin' && <NavButton icon={FileText} label={t.reporting} active={currentView === 'reporting'} onClick={() => setCurrentView('reporting')} />}
            {currentEmployee?.role !== 'Admin' && <NavButton icon={FileText} label={t.myReports} active={currentView === 'myReports'} onClick={() => setCurrentView('myReports')} />}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {currentView === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-2xl font-bold">📊 {t.dashboard}</h2>
              {currentEmployee?.role !== 'Admin' && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-full">
                  👤 Gefiltert: nur deine Projekte ({myAssignedProjectIds.length > 0 ? myAssignedProjectIds.length : 'alle'})
                </span>
              )}
            </div>

            {/* KPI-Karten */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t.hoursToday}</p>
                <p className="text-3xl font-bold text-blue-600">{totalHoursToday.toFixed(1)}h</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t.hoursTotal}</p>
                <p className="text-3xl font-bold text-green-600">{totalHours.toFixed(1)}h</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t.billable}</p>
                <p className="text-3xl font-bold text-emerald-600">{billableHours.toFixed(1)}h</p>
                <p className="text-xs text-gray-400 mt-1">{totalHours > 0 ? Math.round(billableHours / totalHours * 100) : 0}%</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t.nonBillable}</p>
                <p className="text-3xl font-bold text-gray-500">{nonBillableHours.toFixed(1)}h</p>
                <p className="text-xs text-gray-400 mt-1">{totalHours > 0 ? Math.round(nonBillableHours / totalHours * 100) : 0}%</p>
              </div>
            </div>

            {/* Charts Row 1: Bar + Line */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stunden pro Projekt */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-base font-semibold mb-4">📊 Stunden pro Projekt</h3>
                {dashboardData.hoursPerProject.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">Noch keine Einträge</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dashboardData.hoursPerProject} margin={{ top: 4, right: 8, bottom: 40, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip formatter={(v) => [`${v}h`, 'Stunden']} />
                      <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                        {dashboardData.hoursPerProject.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Stunden pro Woche */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-base font-semibold mb-4">📈 Stunden pro Woche (letzte 12 KW)</h3>
                {dashboardData.hoursPerWeek.every(d => d.hours === 0) ? (
                  <p className="text-gray-400 text-sm text-center py-8">Noch keine Einträge</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={dashboardData.hoursPerWeek} margin={{ top: 4, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip formatter={(v) => [`${v}h`, 'Stunden']} />
                      <Line type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Charts Row 2: Pie + Top 5 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Billable vs Non-Billable Pie */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-base font-semibold mb-4">🥧 Billable vs. Non-Billable</h3>
                {dashboardData.billableVsNon.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">Noch keine Einträge</p>
                ) : (
                  <div className="flex items-center gap-6">
                    <ResponsiveContainer width="60%" height={200}>
                      <PieChart>
                        <Pie data={dashboardData.billableVsNon} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                          {dashboardData.billableVsNon.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [`${v}h`]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-3 flex-1">
                      {dashboardData.billableVsNon.map((d, i) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full inline-block" style={{ background: d.color }} />
                              <span className="text-sm text-gray-700">{d.name}</span>
                            </div>
                            <span className="text-sm font-bold">{d.value}h</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full" style={{ width: `${totalHours > 0 ? (d.value / totalHours * 100) : 0}%`, background: d.color }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Top 5 Projekte diesen Monat */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-base font-semibold mb-4">🏆 Top 5 Projekte — {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</h3>
                {dashboardData.top5ThisMonth.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">Keine Einträge diesen Monat</p>
                ) : (
                  <div className="space-y-3">
                    {dashboardData.top5ThisMonth.map((p, i) => {
                      const max = dashboardData.top5ThisMonth[0].hours;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm font-bold text-gray-400 w-5 text-center">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                              <span className="text-sm font-bold text-blue-600 ml-2">{p.hours}h</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="h-2 rounded-full" style={{ width: `${(p.hours / max) * 100}%`, background: CHART_COLORS[i] }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Kalender-Heatmap */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-base font-semibold mb-1">📅 Aktivitäts-Heatmap — letzte 13 Wochen</h3>
              <p className="text-xs text-gray-400 mb-4">Eingetragene Stunden pro Tag</p>
              <div className="overflow-x-auto">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', gap: '3px', minWidth: '520px' }}>
                  {Array.from({ length: 13 }, (_, weekIdx) => (
                    <div key={weekIdx} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {Array.from({ length: 7 }, (_, dow) => {
                        const dayIdx = weekIdx * 7 + dow;
                        const day = dashboardData.heatmapDays[dayIdx];
                        if (!day) return <div key={dow} style={{ width: 28, height: 28 }} />;
                        return (
                          <div
                            key={dow}
                            title={`${day.date}: ${day.hours}h`}
                            style={{
                              width: 28, height: 28,
                              borderRadius: 4,
                              backgroundColor: heatmapColor(day.hours),
                              cursor: day.hours > 0 ? 'pointer' : 'default'
                            }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-gray-400">Weniger</span>
                  {['#f3f4f6', '#bfdbfe', '#60a5fa', '#2563eb', '#1e3a8a'].map((c, i) => (
                    <div key={i} style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: c }} />
                  ))}
                  <span className="text-xs text-gray-400">Mehr</span>
                </div>
              </div>
            </div>

            {/* Recent Entries */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-base font-semibold mb-4">🕐 Letzte Einträge</h3>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">{t.date}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Projekt</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">{t.activity}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">{t.hours}</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">{t.billable}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {timeEntries.slice(0, 8).map(e => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{e.date}</td>
                      <td className="px-4 py-3 text-sm font-medium">{e.project}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{e.activity}</span></td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-600">{e.hours}h</td>
                      <td className="px-4 py-3 text-center">{e.billable ? <CheckCircle className="inline text-emerald-600" size={18} /> : <XCircle className="inline text-gray-400" size={18} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {currentView === 'timeTracking' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">⏱️ {t.timeTracking}</h2>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">{editingEntry ? '✏️ ' + t.editEntry : '➕ ' + t.newEntry}</h3>
              <form onSubmit={editingEntry ? handleUpdateEntry : handleSaveEntry} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">{t.date} *</label><input type="date" value={newEntry.date} onChange={(e) => setNewEntry({...newEntry, date: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Project *</label><select value={newEntry.project} onChange={(e) => setNewEntry({...newEntry, project: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required disabled={editingEntry}><option value="">{t.selectProject}</option>{visibleProjects.filter(p => p.status === 'Active' || p.status === 'Aktiv').map(p => <option key={p.id} value={p.name}>{p.name} ({p.customer})</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">{t.activity} *</label><select value={newEntry.activity} onChange={(e) => setNewEntry({...newEntry, activity: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required><option value="">{t.selectActivity}</option>{activities.map(a => <option key={a.id} value={a.title}>{a.title}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2">{t.hours} *</label><input type="number" step="0.25" min="0" max="24" value={newEntry.hours} onChange={(e) => setNewEntry({...newEntry, hours: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required /></div>
                <div className="md:col-span-2"><label className="flex items-center gap-2"><input type="checkbox" checked={newEntry.billable} onChange={(e) => setNewEntry({...newEntry, billable: e.target.checked})} className="w-5 h-5" /><span className="text-sm font-medium">💰 {t.billableCheckbox}</span></label></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-2">{t.description}</label><textarea value={newEntry.description} onChange={(e) => setNewEntry({...newEntry, description: e.target.value})} rows="3" className="w-full px-4 py-2 border rounded-lg" /></div>
                <div className="md:col-span-2">
                  <div className="flex gap-3 flex-wrap">
                    <button type="submit" disabled={loading} className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                      <Save size={20} />
                      {loading ? t.loading : (editingEntry ? t.update : t.save)}
                    </button>
                    {!editingEntry && (
                      <button
                        type="button"
                        onClick={handleSaveTemplate}
                        className="flex items-center gap-2 px-4 py-3 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-sm font-medium"
                        title="Als Template/Favorit speichern"
                      >
                        <Star size={16} /> Als Template speichern
                      </button>
                    )}
                    {editingEntry && (
                      <button type="button" onClick={handleCancelEdit} className="flex items-center gap-2 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600">
                        <X size={20} />
                        {t.cancel}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="bg-white rounded-lg shadow-sm border">
              {/* Header + Bulk-Toolbar */}
              <div className="px-6 py-4 border-b flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-lg font-semibold">📋 My Time Entries</h3>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                      {selectedIds.size} ausgewählt
                    </span>
                    <button
                      onClick={() => handleBulkSetBillable(true)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                    >
                      <CheckCircle size={14} /> Billable setzen
                    </button>
                    <button
                      onClick={() => handleBulkSetBillable(false)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                    >
                      <XCircle size={14} /> Non-Billable setzen
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} /> Löschen
                    </button>
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2"
                    >
                      ✕ Auswahl aufheben
                    </button>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                {(() => {
                  const sorted = sortBy(timeEntries, teSort);
                  const allSelected = sorted.length > 0 && selectedIds.size === sorted.length;
                  return (
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {/* Select-All Checkbox */}
                          <th className="pl-4 pr-2 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !allSelected; }}
                              onChange={() => toggleSelectAll(sorted)}
                              className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                            />
                          </th>
                          <Th label={t.date}    sortKey="date"     sort={teSort} onSort={makeToggle(setTeSort)} />
                          <Th label="Projekt"   sortKey="project"  sort={teSort} onSort={makeToggle(setTeSort)} />
                          <Th label={t.activity} sortKey="activity" sort={teSort} onSort={makeToggle(setTeSort)} />
                          <Th label={t.hours}   sortKey="hours"    sort={teSort} onSort={makeToggle(setTeSort)} />
                          <Th label={t.billable} sortKey="billable" sort={teSort} onSort={makeToggle(setTeSort)} />
                          <th className="px-4 py-3 text-left text-sm font-semibold">{t.description}</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sorted.map(e => {
                          const isSelected = selectedIds.has(e.id);
                          return (
                            <tr
                              key={e.id}
                              className={`transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                            >
                              <td className="pl-4 pr-2 py-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(e.id)}
                                  className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                                />
                              </td>
                              <td className="px-4 py-3 text-sm">{e.date}</td>
                              <td className="px-4 py-3 text-sm font-medium">{e.project}</td>
                              <td className="px-4 py-3 text-sm"><span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{e.activity}</span></td>
                              <td className="px-4 py-3 text-sm font-semibold text-blue-600">{e.hours}h</td>
                              <td className="px-4 py-3">{e.billable ? <CheckCircle className="text-emerald-600" size={18} /> : <XCircle className="text-gray-400" size={18} />}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{e.description}</td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1 justify-end">
                                  <button onClick={() => handleDuplicate(e)} className="p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded" title="Duplizieren (heute)"><Copy size={15} /></button>
                                  <button onClick={() => handleEditEntry(e)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title={t.edit}><Edit2 size={15} /></button>
                                  <button onClick={() => handleDeleteEntry(e.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title={t.delete}><Trash2 size={15} /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {currentView === 'customers' && (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold">🏢 {t.customers}</h2>
    <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Name"            sortKey="name"         sort={custSort} onSort={makeToggle(setCustSort)} className="px-6" />
            <Th label="Contact Person"  sortKey="contactName"  sort={custSort} onSort={makeToggle(setCustSort)} className="px-6" />
            <th className="px-6 py-3 text-left text-sm font-semibold">Contact Email</th>
            <Th label="Customer Since"  sortKey="customerSince" sort={custSort} onSort={makeToggle(setCustSort)} className="px-6" />
            <Th label="Status"          sortKey="status"       sort={custSort} onSort={makeToggle(setCustSort)} className="px-6" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {sortBy(customers, custSort).map(c => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-semibold">{c.name}</td>
              <td className="px-6 py-4 text-sm">{c.contactName || '-'}</td>
              <td className="px-6 py-4 text-sm">
                {c.contactEmail ? <a href={`mailto:${c.contactEmail}`} className="text-blue-600 hover:underline">{c.contactEmail}</a> : '-'}
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">{c.customerSince || '-'}</td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${c.status === 'Active' || c.status === 'Aktiv' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {c.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

        {currentView === 'projects' && (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold">📁 {t.projects}</h2>
      <button
        onClick={() => setShowOnlyMyProjects(v => !v)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          showOnlyMyProjects
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {showOnlyMyProjects ? '👤 Nur meine Projekte' : '🌐 Alle Projekte'}
      </button>
    </div>
    {/* Sort-Leiste */}
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <span>Sortieren:</span>
      {[['name','Name'],['customer','Kunde'],['budget','Budget'],['status','Status']].map(([key, label]) => (
        <button
          key={key}
          onClick={() => makeToggle(setProjSort)(key)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors ${
            projSort.key === key ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          {label}
          <span className="text-xs">{projSort.key === key ? (projSort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
        </button>
      ))}
    </div>

    {visibleProjects.length === 0 && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
        ⚠️ Keine Projekte zugewiesen. {currentEmployee?.role === 'Admin' ? 'Gehe zu Mitarbeiter → Projektzuordnung.' : 'Bitte einen Admin um Projektzuordnung.'}
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {sortBy(visibleProjects, projSort).map(p => (
        <div key={p.id} className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold">{p.name}</h3>
              <p className="text-sm text-gray-600">{p.customer}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">{p.status}</span>
              {myAssignedProjectIds.includes(Number(p.id)) && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">👤 Zugewiesen</span>
              )}
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-semibold">{p.type}</span>
            </div>
            {p.rate > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Rate:</span>
                <span className="font-semibold">CHF {p.rate}</span>
              </div>
            )}
            {p.budget > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Budget:</span>
                <span className="font-semibold">CHF {p.budget.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

        {currentView === 'employees' && currentEmployee?.role === 'Admin' && (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold">👥 {t.employees}</h2>
    <div className="bg-white rounded-lg shadow-sm border">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th label="Name"         sortKey="name"       sort={empSort} onSort={makeToggle(setEmpSort)} className="px-6" />
            <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
            <Th label="Role"         sortKey="role"       sort={empSort} onSort={makeToggle(setEmpSort)} className="px-6" />
            <Th label="Hourly Rate"  sortKey="hourlyRate" sort={empSort} onSort={makeToggle(setEmpSort)} className="px-6" />
            <Th label="Status"       sortKey="status"     sort={empSort} onSort={makeToggle(setEmpSort)} className="px-6" />
            <th className="px-6 py-3 text-left text-sm font-semibold">Projektzuordnung</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {sortBy(employees, empSort).map(e => (
            <tr key={e.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-semibold">{e.name}</td>
              <td className="px-6 py-4 text-sm">
                {e.email ? (
                  <a href={`mailto:${e.email}`} className="text-blue-600 hover:underline">
                    {e.email}
                  </a>
                ) : '-'}
              </td>
              <td className="px-6 py-4 text-sm">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  e.role === 'Admin' 
                    ? 'bg-purple-100 text-purple-800' 
                    : e.role === 'Project Manager'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {e.role}
                </span>
              </td>
              <td className="px-6 py-4 text-sm font-semibold">CHF {e.hourlyRate.toFixed(2)}/h</td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  e.status === 'Aktiv' || e.status === 'Active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {e.status}
                </span>
              </td>
              <td className="px-6 py-4">
                <button
                  onClick={() => setManagingEmployee(e)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  📁 Projekte ({projectAssignments.filter(a => Number(a.employeeId) === Number(e.id)).length})
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

        {/* Admin-Modal: Projektzuordnung */}
        {managingEmployee && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h3 className="text-lg font-bold">📁 Projektzuordnung</h3>
          <p className="text-sm text-gray-500">{managingEmployee.name} · {managingEmployee.role}</p>
        </div>
        <button onClick={() => setManagingEmployee(null)} className="p-2 hover:bg-gray-100 rounded-lg">
          <X size={20} />
        </button>
      </div>
      <div className="overflow-y-auto p-6 space-y-3 flex-1">
        {projects.filter(p => p.status === 'Active' || p.status === 'Aktiv').length === 0 && (
          <p className="text-gray-500 text-sm">Keine aktiven Projekte vorhanden.</p>
        )}
        {projects
          .filter(p => p.status === 'Active' || p.status === 'Aktiv')
          .map(p => {
            const isAssigned = projectAssignments.some(
              a => Number(a.employeeId) === Number(managingEmployee.id) && Number(a.projectId) === Number(p.id)
            );
            return (
              <label key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                isAssigned ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  checked={isAssigned}
                  onChange={() => handleToggleAssignment(managingEmployee, p)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.customer} · {p.type}</p>
                </div>
                {isAssigned && <span className="text-blue-600 text-xs font-medium">✓ Zugewiesen</span>}
              </label>
            );
          })}
      </div>
      <div className="p-4 border-t bg-gray-50 rounded-b-xl">
        <p className="text-xs text-gray-500 text-center">
          {projectAssignments.filter(a => Number(a.employeeId) === Number(managingEmployee.id)).length} Projekte zugewiesen
        </p>
      </div>
    </div>
  </div>
)}

        {currentView === 'myReports' && currentEmployee?.role !== 'Admin' && (() => {
          const myEntries = getMyReportEntries();
          const totalH = myEntries.reduce((sum, e) => sum + e.hours, 0);
          const billH = myEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-2xl font-bold">📄 {t.myReports}</h2>
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-full">
                  👤 Nur deine Projekte ({myReportProjects.length}) und deine eigenen Stunden
                </span>
              </div>

              {myReportProjects.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
                  Dir sind noch keine Projekte zugewiesen. Bitte melde dich bei einem Admin.
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Projekt</label>
                      <select value={myReportFilter.project} onChange={(e) => setMyReportFilter({ ...myReportFilter, project: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                        <option value="">Alle meine Projekte</option>
                        {myReportProjects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Von</label>
                      <input type="date" value={myReportFilter.startDate} onChange={(e) => setMyReportFilter({ ...myReportFilter, startDate: e.target.value })} className="w-full px-4 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bis</label>
                      <input type="date" value={myReportFilter.endDate} onChange={(e) => setMyReportFilter({ ...myReportFilter, endDate: e.target.value })} className="w-full px-4 py-2 border rounded-lg" />
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap items-center">
                    <span className="text-sm text-gray-500">Zeitraum:</span>
                    {[['thisMonth', 'Dieser Monat'], ['lastMonth', 'Letzter Monat'], ['thisYear', 'Dieses Jahr'], ['all', 'Alles']].map(([id, label]) => (
                      <button key={id} onClick={() => setMyReportPeriod(id)} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">{label}</button>
                    ))}
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={myReportFilter.onlyBillable} onChange={(e) => setMyReportFilter({ ...myReportFilter, onlyBillable: e.target.checked })} className="w-5 h-5 rounded" />
                    <span className="text-sm font-medium text-gray-700">💰 Nur verrechenbare Stunden</span>
                  </label>

                  <div className="bg-gray-50 rounded-xl border p-4">
                    <div className="flex gap-6 flex-wrap">
                      <div><p className="text-xs text-gray-500">Einträge</p><p className="text-2xl font-bold">{myEntries.length}</p></div>
                      <div><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-blue-600">{totalH.toFixed(1)}h</p></div>
                      <div><p className="text-xs text-gray-500">Verrechenbar</p><p className="text-2xl font-bold text-emerald-600">{billH.toFixed(1)}h</p></div>
                      <div><p className="text-xs text-gray-500">Nicht verrechenbar</p><p className="text-2xl font-bold text-gray-500">{(totalH - billH).toFixed(1)}h</p></div>
                    </div>
                  </div>

                  <button onClick={handleMyReportPDF} disabled={loading || myEntries.length === 0} className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400">
                    📄 PDF-Report erstellen
                  </button>
                  {myEntries.length === 0 && <p className="text-sm text-gray-500">Keine Einträge im gewählten Zeitraum.</p>}
                </div>
              )}
            </div>
          );
        })()}

        {currentView === 'reporting' && (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold">📊 {t.reporting}</h2>

    {/* ── Report-Typ Tabs ── */}
    <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-full overflow-x-auto">
      {[
        { id: 'custom',   icon: '📄', label: 'Custom Report' },
        { id: 'monthly',  icon: '📅', label: 'Monatsbericht' },
        { id: 'customer', icon: '🏢', label: 'Kunden-Zusammenfassung' },
        { id: 'employee', icon: '👤', label: 'Mitarbeiter-Report' },
        { id: 'budget',   icon: '💰', label: 'Projekt-Fortschritt' },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => setReportType(tab.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            reportType === tab.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {tab.icon} {tab.label}
        </button>
      ))}
    </div>

    {/* ══════════════════════════════════════════════════════
        TAB 1: Custom Report (bestehend)
    ══════════════════════════════════════════════════════ */}
    {reportType === 'custom' && (
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
        <h3 className="text-lg font-semibold">📄 Custom Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
            <select value={reportFilter.customer} onChange={(e) => setReportFilter({...reportFilter, customer: e.target.value, project: ''})} className="w-full px-4 py-2 border rounded-lg">
              <option value="">All Customers</option>
              {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
            <select value={reportFilter.project} onChange={(e) => setReportFilter({...reportFilter, project: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
              <option value="">All Projects</option>
              {projects.filter(p => !reportFilter.customer || p.customer === reportFilter.customer).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input type="date" value={reportFilter.startDate} onChange={(e) => setReportFilter({...reportFilter, startDate: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input type="date" value={reportFilter.endDate} onChange={(e) => setReportFilter({...reportFilter, endDate: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={reportFilter.onlyBillable} onChange={(e) => setReportFilter({...reportFilter, onlyBillable: e.target.checked})} className="w-5 h-5 rounded" />
              <span className="text-sm font-medium text-gray-700">💰 Only billable hours</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={handlePDFExport} disabled={loading} className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400">📄 Export PDF</button>
          <button onClick={handleExcelExport} disabled={loading} className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400">📊 Export Excel</button>
        </div>
        {(() => {
          const filtered = getFilteredReportEntries();
          const totalH = filtered.reduce((sum, e) => sum + e.hours, 0);
          const billH = filtered.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
          return (
            <div className="bg-gray-50 rounded-xl border p-4 space-y-4">
              <div className="flex gap-6 flex-wrap">
                <div><p className="text-xs text-gray-500">Einträge</p><p className="text-2xl font-bold">{filtered.length}</p></div>
                <div><p className="text-xs text-gray-500">Total</p><p className="text-2xl font-bold text-blue-600">{totalH.toFixed(1)}h</p></div>
                <div><p className="text-xs text-gray-500">Billable</p><p className="text-2xl font-bold text-emerald-600">{billH.toFixed(1)}h</p></div>
                <div><p className="text-xs text-gray-500">Non-Billable</p><p className="text-2xl font-bold text-gray-500">{(totalH - billH).toFixed(1)}h</p></div>
              </div>
              {filtered.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-white">
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-left font-semibold">Project</th>
                      <th className="px-3 py-2 text-left font-semibold">Activity</th>
                      <th className="px-3 py-2 text-left font-semibold">Employee</th>
                      <th className="px-3 py-2 text-right font-semibold">Hours</th>
                      <th className="px-3 py-2 text-center font-semibold">Billable</th>
                      <th className="px-3 py-2 text-left font-semibold">Description</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {filtered.map(e => (
                        <tr key={e.id} className="hover:bg-white">
                          <td className="px-3 py-2">{e.date}</td>
                          <td className="px-3 py-2 font-medium">{e.project}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">{e.activity}</span></td>
                          <td className="px-3 py-2 text-gray-600">{e.employee || '-'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-blue-600">{e.hours}h</td>
                          <td className="px-3 py-2 text-center">{e.billable ? <CheckCircle size={14} className="inline text-emerald-600" /> : <XCircle size={14} className="inline text-gray-400" />}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{e.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    )}

    {/* ══════════════════════════════════════════════════════
        TAB 2: Monatsbericht
    ══════════════════════════════════════════════════════ */}
    {reportType === 'monthly' && (() => {
      const monthMap = {};
      timeEntries.forEach(e => {
        if (!e.date) return;
        const m = e.date.slice(0, 7); // YYYY-MM
        if (!monthMap[m]) monthMap[m] = { total: 0, billable: 0, entries: 0, projects: new Set() };
        monthMap[m].total += e.hours;
        if (e.billable) monthMap[m].billable += e.hours;
        monthMap[m].entries++;
        if (e.project) monthMap[m].projects.add(e.project);
      });
      const months = Object.entries(monthMap)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([month, d]) => ({ month, ...d, projects: d.projects.size }));
      const maxH = Math.max(...months.map(m => m.total), 1);

      return (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <h3 className="text-lg font-semibold">📅 Monatsbericht</h3>
          {months.length === 0 ? <p className="text-gray-400 py-8 text-center">Keine Daten vorhanden.</p> : (
            <div className="space-y-3">
              {months.map(m => {
                const label = new Date(m.month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
                const billPct = m.total > 0 ? Math.round(m.billable / m.total * 100) : 0;
                return (
                  <div key={m.month} className="border rounded-xl p-4 hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{label}</h4>
                      <div className="flex gap-4 text-sm">
                        <span className="text-gray-500">{m.entries} Einträge · {m.projects} Projekte</span>
                        <span className="font-bold text-blue-600">{m.total.toFixed(1)}h</span>
                      </div>
                    </div>
                    {/* Gesamt-Balken */}
                    <div className="w-full bg-gray-100 rounded-full h-3 mb-2 relative overflow-hidden">
                      <div className="h-3 rounded-full bg-blue-200 absolute left-0" style={{ width: `${(m.total / maxH) * 100}%` }} />
                      <div className="h-3 rounded-full bg-emerald-500 absolute left-0" style={{ width: `${(m.billable / maxH) * 100}%` }} />
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Billable: {m.billable.toFixed(1)}h ({billPct}%)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" /> Non-Billable: {(m.total - m.billable).toFixed(1)}h</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    })()}

    {/* ══════════════════════════════════════════════════════
        TAB 3: Kunden-Zusammenfassung
    ══════════════════════════════════════════════════════ */}
    {reportType === 'customer' && (() => {
      const selCust = reportCustFilter || customers[0]?.name || '';
      const setSelCust = setReportCustFilter;
      const custProjects = projects.filter(p => p.customer === selCust);
      const custEntries = timeEntries.filter(e => {
        const proj = projects.find(p => p.name === e.project);
        return proj?.customer === selCust;
      });
      const totalH = custEntries.reduce((sum, e) => sum + e.hours, 0);
      const billH = custEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);

      return (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold">🏢 Kunden-Zusammenfassung</h3>
            <select value={selCust} onChange={e => setSelCust(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
              {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Projekte', value: custProjects.length, color: 'text-blue-600' },
              { label: 'Total Stunden', value: `${totalH.toFixed(1)}h`, color: 'text-gray-900' },
              { label: 'Billable', value: `${billH.toFixed(1)}h`, color: 'text-emerald-600' },
              { label: 'Billable %', value: `${totalH > 0 ? Math.round(billH / totalH * 100) : 0}%`, color: 'text-blue-600' },
            ].map((kpi, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 border">
                <p className="text-xs text-gray-500 mb-1">{kpi.label}</p>
                <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Projekte des Kunden */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3">Projekte</h4>
            {custProjects.length === 0 ? <p className="text-gray-400 text-sm">Keine Projekte für diesen Kunden.</p> : (
              <div className="space-y-2">
                {custProjects.map(p => {
                  const projEntries = custEntries.filter(e => e.project === p.name);
                  const projH = projEntries.reduce((sum, e) => sum + e.hours, 0);
                  const projBill = projEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
                  const budgetPct = p.budget > 0 && p.rate > 0 ? Math.min(Math.round((projH * p.rate) / p.budget * 100), 100) : null;
                  return (
                    <div key={p.id} className="flex items-center gap-4 p-3 rounded-lg border hover:border-blue-200">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.type} · {p.status}</p>
                      </div>
                      <div className="text-right text-sm shrink-0">
                        <p className="font-bold text-blue-600">{projH.toFixed(1)}h</p>
                        <p className="text-xs text-emerald-600">{projBill.toFixed(1)}h bill.</p>
                      </div>
                      {budgetPct !== null && (
                        <div className="w-24 shrink-0">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Budget</span><span>{budgetPct}%</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className={`h-2 rounded-full ${budgetPct > 90 ? 'bg-red-500' : budgetPct > 75 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                              style={{ width: `${budgetPct}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Letzte Einträge */}
          {custEntries.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Letzte Einträge</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <th className="px-3 py-2 text-left font-semibold">Datum</th>
                    <th className="px-3 py-2 text-left font-semibold">Projekt</th>
                    <th className="px-3 py-2 text-left font-semibold">Aktivität</th>
                    <th className="px-3 py-2 text-left font-semibold">Mitarbeiter</th>
                    <th className="px-3 py-2 text-right font-semibold">Stunden</th>
                    <th className="px-3 py-2 text-center font-semibold">Billable</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {[...custEntries].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 20).map(e => (
                      <tr key={e.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{e.date}</td>
                        <td className="px-3 py-2 font-medium">{e.project}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">{e.activity}</span></td>
                        <td className="px-3 py-2 text-gray-500">{e.employee || '-'}</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-600">{e.hours}h</td>
                        <td className="px-3 py-2 text-center">{e.billable ? <CheckCircle size={14} className="inline text-emerald-600" /> : <XCircle size={14} className="inline text-gray-400" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    })()}

    {/* ══════════════════════════════════════════════════════
        TAB 4: Mitarbeiter-Report
    ══════════════════════════════════════════════════════ */}
    {reportType === 'employee' && (() => {
      const empMap = {};
      timeEntries.forEach(e => {
        const key = e.employee || 'Unbekannt';
        if (!empMap[key]) empMap[key] = { total: 0, billable: 0, entries: 0, projects: new Set() };
        empMap[key].total += e.hours;
        if (e.billable) empMap[key].billable += e.hours;
        empMap[key].entries++;
        if (e.project) empMap[key].projects.add(e.project);
      });
      const empRows = Object.entries(empMap)
        .map(([name, d]) => {
          const empData = employees.find(e => e.name === name);
          const cost = empData?.hourlyRate ? d.billable * empData.hourlyRate : null;
          return { name, ...d, projects: d.projects.size, rate: empData?.hourlyRate || 0, cost };
        })
        .sort((a, b) => b.total - a.total);
      const maxH = Math.max(...empRows.map(r => r.total), 1);

      return (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <h3 className="text-lg font-semibold">👤 Mitarbeiter-Report</h3>

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 border">
              <p className="text-xs text-gray-500">Mitarbeiter aktiv</p>
              <p className="text-2xl font-bold text-blue-600">{empRows.length}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border">
              <p className="text-xs text-gray-500">Total Stunden</p>
              <p className="text-2xl font-bold">{empRows.reduce((s, r) => s + r.total, 0).toFixed(1)}h</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border">
              <p className="text-xs text-gray-500">Billable gesamt</p>
              <p className="text-2xl font-bold text-emerald-600">{empRows.reduce((s, r) => s + r.billable, 0).toFixed(1)}h</p>
            </div>
          </div>

          {/* Mitarbeiter-Tabelle */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Mitarbeiter</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Total</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Billable</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Non-Bill.</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Bill. %</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Projekte</th>
                  {currentEmployee?.role === 'Admin' && <th className="px-4 py-3 text-right text-sm font-semibold">Umsatz (bill.)</th>}
                  <th className="px-4 py-3 text-left text-sm font-semibold w-40">Verteilung</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {empRows.map(r => (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-600">{r.total.toFixed(1)}h</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{r.billable.toFixed(1)}h</td>
                    <td className="px-4 py-3 text-right text-gray-500">{(r.total - r.billable).toFixed(1)}h</td>
                    <td className="px-4 py-3 text-right">{r.total > 0 ? Math.round(r.billable / r.total * 100) : 0}%</td>
                    <td className="px-4 py-3 text-right">{r.projects}</td>
                    {currentEmployee?.role === 'Admin' && (
                      <td className="px-4 py-3 text-right font-semibold">
                        {r.cost !== null ? `CHF ${r.cost.toLocaleString('de-CH', { minimumFractionDigits: 0 })}` : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="w-full bg-gray-100 rounded-full h-2 relative overflow-hidden">
                        <div className="h-2 bg-blue-200 rounded-full absolute left-0" style={{ width: `${(r.total / maxH) * 100}%` }} />
                        <div className="h-2 bg-emerald-500 rounded-full absolute left-0" style={{ width: `${(r.billable / maxH) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    })()}

    {/* ══════════════════════════════════════════════════════
        TAB 5: Projekt-Fortschritt (Budget vs Actual)
    ══════════════════════════════════════════════════════ */}
    {reportType === 'budget' && (() => {
      const projData = projects.map(p => {
        const entries = timeEntries.filter(e => e.project === p.name);
        const actualH = entries.reduce((s, e) => s + e.hours, 0);
        const billH = entries.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
        const actualCost = p.rate > 0 ? billH * p.rate : null;
        const budgetPct = p.budget > 0 && actualCost !== null ? Math.round(actualCost / p.budget * 100) : null;
        return { ...p, actualH, billH, actualCost, budgetPct, entries: entries.length };
      }).sort((a, b) => (b.budgetPct ?? -1) - (a.budgetPct ?? -1));

      return (
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <h3 className="text-lg font-semibold">💰 Projekt-Fortschritt — Budget vs. Actual</h3>

          <div className="space-y-4">
            {projData.map(p => {
              const hasbudget = p.budget > 0 && p.actualCost !== null;
              const pct = p.budgetPct ?? 0;
              const barColor = pct > 100 ? 'bg-red-600' : pct > 90 ? 'bg-red-400' : pct > 75 ? 'bg-amber-400' : 'bg-emerald-500';
              return (
                <div key={p.id} className={`rounded-xl border p-4 ${pct > 100 ? 'border-red-200 bg-red-50' : pct > 90 ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between mb-3 gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 truncate">{p.name}</h4>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${p.status === 'Active' || p.status === 'Aktiv' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                        {pct > 100 && <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">⚠ Überbucht</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{p.customer} · {p.type} · {p.entries} Einträge</p>
                    </div>
                    <div className="text-right shrink-0 space-y-0.5">
                      <p className="text-sm font-bold text-blue-600">{p.actualH.toFixed(1)}h gearbeitet</p>
                      {p.rate > 0 && <p className="text-xs text-gray-500">CHF {p.rate}/h</p>}
                      {hasbudget && (
                        <p className="text-xs font-semibold">
                          <span className={pct > 100 ? 'text-red-600' : 'text-gray-700'}>
                            CHF {(p.actualCost || 0).toLocaleString('de-CH', { minimumFractionDigits: 0 })}
                          </span>
                          <span className="text-gray-400"> / CHF {p.budget.toLocaleString('de-CH', { minimumFractionDigits: 0 })}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {hasbudget ? (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Budget-Verbrauch</span>
                        <span className={`font-bold ${pct > 100 ? 'text-red-600' : pct > 90 ? 'text-amber-600' : 'text-emerald-600'}`}>{pct}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>CHF 0</span>
                        <span>CHF {p.budget.toLocaleString('de-CH', { minimumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      {p.budget > 0 ? 'Rate nicht definiert — Prozent nicht berechenbar.' : 'Kein Budget definiert.'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legende */}
          <div className="flex gap-4 text-xs text-gray-500 pt-2 border-t flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> &lt; 75%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> 75–90%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block" /> 90–100%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-600 inline-block" /> &gt; 100% (Überbucht)</span>
          </div>
        </div>
      );
    })()}

  </div>
)}

</main>
    </div>
);
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <TimeTrackerApp />
    </MsalProvider>
  );
}

export default App;